import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/apple/Reveal';
import { ArrowLink, Container, PillButton } from '@/components/apple/ui';
import { CATEGORIAS, PROJETOS, img, type Categoria, type Projeto } from '@/lib/projetos';
import { SITE_URL, WHATSAPP_NUMBER } from '@/config/site';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

type Filtro = 'Todos' | Categoria;

interface Galeria {
  projeto: Projeto;
  index: number;
}

function Lightbox({ galeria, onClose, onMove }: { galeria: Galeria; onClose: () => void; onMove: (d: number) => void }) {
  const { projeto, index } = galeria;
  const total = projeto.fotos.length;
  const touchX = useRef<number | null>(null);
  const thumbs = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onMove(-1);
      if (e.key === 'ArrowRight') onMove(1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onMove]);

  useEffect(() => {
    const el = thumbs.current?.children[index] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    // Pré-carrega a próxima foto
    const next = new Image();
    next.src = img(projeto.fotos[(index + 1) % total], 2000);
  }, [index, projeto, total]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`Galeria — ${projeto.nome}`}
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) onMove(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <div className="flex items-center justify-between gap-4 px-4 md:px-6 h-14 md:h-16 shrink-0 text-white">
        <div className="min-w-0">
          <p className="text-[15px] md:text-[17px] font-semibold tracking-[-0.02em] truncate">{projeto.nome}</p>
          <p className="text-[12px] md:text-[13px] text-white/60">
            {projeto.cidade} · {index + 1} de {total}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Fechar galeria"
        >
          <i className="ri-close-line text-[22px]"></i>
        </button>
      </div>

      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 md:px-20">
        <img
          key={projeto.fotos[index]}
          src={img(projeto.fotos[index], 2000)}
          alt={`${projeto.nome} — foto ${index + 1} de ${total}`}
          className="max-w-full max-h-full object-contain animate-fade-in"
        />
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => onMove(-1)}
              className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Foto anterior"
            >
              <i className="ri-arrow-left-s-line text-[26px]"></i>
            </button>
            <button
              type="button"
              onClick={() => onMove(1)}
              className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Próxima foto"
            >
              <i className="ri-arrow-right-s-line text-[26px]"></i>
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <div ref={thumbs} className="shrink-0 flex gap-2 overflow-x-auto px-4 md:px-6 py-4 justify-start md:justify-center [scrollbar-width:none]">
          {projeto.fotos.map((f, i) => (
            <button
              key={f}
              type="button"
              onClick={() => onMove(i - index)}
              className={`shrink-0 rounded-[8px] overflow-hidden transition-opacity ${i === index ? 'opacity-100 ring-2 ring-white' : 'opacity-40 hover:opacity-80'}`}
              aria-label={`Ver foto ${i + 1}`}
            >
              <img src={img(f, 160)} alt="" loading="lazy" className="w-14 h-10 md:w-16 md:h-12 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CardProjeto({ p, grande, onOpen }: { p: Projeto; grande?: boolean; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="group block w-full text-left">
      <div className="overflow-hidden rounded-[18px] md:rounded-[24px] bg-[#F5F5F7]">
        <img
          src={img(p.capa, grande ? 1800 : 1100)}
          alt={p.alt}
          loading="lazy"
          decoding="async"
          className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03] ${
            grande ? 'aspect-[4/3] md:aspect-[21/9]' : 'aspect-[4/3]'
          }`}
        />
      </div>
      <div className="mt-4 md:mt-5 px-1">
        <p className="text-[12px] font-semibold text-[#C4500A]">{p.categoria}</p>
        <h2 className={`mt-1 font-semibold tracking-[-0.025em] text-[#1D1D1F] ${grande ? 'text-[24px] md:text-[32px]' : 'text-[19px] md:text-[21px]'}`}>
          {p.nome}
        </h2>
        <p className="mt-0.5 text-[14px] md:text-[15px] text-[#6E6E73]">{p.cidade}</p>
        <p className="mt-2 text-[15px] leading-[1.45] text-[#424245] max-w-xl">{p.resumo}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-[14px] md:text-[15px] text-[#C4500A] group-hover:underline underline-offset-4">
          Ver {p.fotos.length} {p.fotos.length === 1 ? 'foto' : 'fotos'} <span aria-hidden="true">›</span>
        </span>
      </div>
    </button>
  );
}

export default function CasesPage() {
  const [filtro, setFiltro] = useState<Filtro>('Todos');
  const [galeria, setGaleria] = useState<Galeria | null>(null);

  useSEO({
    title: 'Projetos Realizados | Mobiliário Corporativo em Pernambuco – HomeOffice Premium',
    description:
      'Fotos reais de projetos de mobiliário corporativo e institucional entregues pela HomeOffice Premium: Copergás, Sicoob, Prefeitura de Caruaru, Câmaras de Vereadores e mais.',
    keywords: 'projetos mobiliário corporativo, cases Caruaru, mobiliário institucional Pernambuco, portfólio HomeOffice Premium',
    path: '/cases',
    ogTitle: 'Projetos Realizados | HomeOffice Premium',
    ogDescription: 'Fotos reais de ambientes corporativos e institucionais entregues em Pernambuco.',
  });

  useSchemaLD({
    id: 'schema-cases',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Projetos realizados – HomeOffice Premium',
      url: `${SITE_URL}/cases`,
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: PROJETOS.length,
        itemListElement: PROJETOS.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${p.nome} — ${p.cidade}`,
          image: p.capa,
        })),
      },
    },
  });

  const lista = useMemo(() => (filtro === 'Todos' ? PROJETOS : PROJETOS.filter((p) => p.categoria === filtro)), [filtro]);
  const filtros: Filtro[] = useMemo(
    () => ['Todos', ...CATEGORIAS.filter((c) => PROJETOS.some((p) => p.categoria === c))],
    []
  );

  const fechar = useCallback(() => setGaleria(null), []);
  const mover = useCallback((d: number) => {
    setGaleria((g) => {
      if (!g) return g;
      const n = g.projeto.fotos.length;
      return { ...g, index: (((g.index + d) % n) + n) % n };
    });
  }, []);

  const [destaque, ...resto] = lista;

  return (
    <div className="min-h-screen bg-white text-[#1D1D1F]">
      <Navbar />

      <main className="pt-14 md:pt-16">
        <section className="pt-14 md:pt-24 pb-10 md:pb-14 text-center">
          <Container>
            <p className="text-[14px] md:text-[17px] font-semibold text-[#C4500A] animate-reveal" style={{ opacity: 0 }}>
              Projetos
            </p>
            <h1
              className="mt-2 text-[40px] sm:text-[56px] md:text-[72px] leading-[1.04] tracking-[-0.045em] font-semibold animate-reveal delay-100"
              style={{ opacity: 0 }}
            >
              Projetos que falam por si.
            </h1>
            <p
              className="mt-5 max-w-2xl mx-auto text-[17px] md:text-[21px] leading-[1.4] tracking-[-0.015em] text-[#6E6E73] animate-reveal delay-200"
              style={{ opacity: 0 }}
            >
              Fotos reais de ambientes corporativos e institucionais que entregamos em Pernambuco.
            </p>

            <div
              className="mt-10 inline-flex max-w-full overflow-x-auto [scrollbar-width:none] rounded-full bg-[#F5F5F7] p-1 animate-reveal delay-300"
              style={{ opacity: 0 }}
              role="tablist"
              aria-label="Filtrar projetos por categoria"
            >
              {filtros.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filtro === f}
                  onClick={() => setFiltro(f)}
                  className={`shrink-0 px-4 py-2 rounded-full text-[13px] md:text-[14px] whitespace-nowrap transition-colors ${
                    filtro === f ? 'bg-white text-[#1D1D1F] shadow-[0_1px_4px_rgba(0,0,0,0.08)]' : 'text-[#6E6E73] hover:text-[#1D1D1F]'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Container>
        </section>

        <section className="pb-24 md:pb-36">
          <Container>
            {destaque && (
              <Reveal key={`d-${filtro}`}>
                <CardProjeto p={destaque} grande onOpen={() => setGaleria({ projeto: destaque, index: 0 })} />
              </Reveal>
            )}
            <div className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-14 md:gap-y-20">
              {resto.map((p, i) => (
                <Reveal key={`${filtro}-${p.id}`} delay={((i % 2) + 1) as 1 | 2}>
                  <CardProjeto p={p} onOpen={() => setGaleria({ projeto: p, index: 0 })} />
                </Reveal>
              ))}
            </div>
          </Container>
        </section>

        <section className="bg-[#F5F5F7] py-24 md:py-32 text-center">
          <Container>
            <h2 className="text-[34px] sm:text-[44px] md:text-[56px] leading-[1.06] tracking-[-0.04em] font-semibold">
              Quer um projeto assim?
            </h2>
            <p className="mt-4 text-[17px] md:text-[21px] text-[#6E6E73]">Conte o que sua empresa precisa. O proprietário analisa pessoalmente.</p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-7">
              <PillButton
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                  'Olá Ednilson! Vi os projetos no site da HomeOffice Premium e gostaria de conversar sobre o meu.'
                )}`}
              >
                Falar sobre meu projeto
              </PillButton>
              <ArrowLink to="/orcamento">Solicitar orçamento</ArrowLink>
            </div>
          </Container>
        </section>
      </main>

      <Footer />

      {galeria && <Lightbox galeria={galeria} onClose={fechar} onMove={mover} />}
    </div>
  );
}
